/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRouter } from '@/hooks/hooks';
import style from './Analysis.module.less';
import { Button, Divider, Dropdown, Menu, message } from 'antd';
import Wrapper from '@/common/wrapper/Wrapper';
import { useEffect, useMemo, useState } from 'react';
import {addLogoPt, exportCourseScoreZip, getAllCourseAvg } from '@/service/exam/analysis';
import { getRateConfig } from '@/service/exam/reportSetting';
import {
  getAllCourseAvgColumns,
  ColumnConfig,
  courseModuleTypeRecord,
  getColumnSetting,
  ALL_COURSE_AVG_COLUMN_SETTING_CONFIG
} from './const';
import {courseRecord, groupRecord, schoolAreaRecord, schoolAreaSeq, townRecord, townSeq} from '@/utils/const';
import { instance } from '@/service';
import { downloadFileByBlob, downloadFileByUrl } from '@/pages/common/importExcel/const/const';
import { getWorkbook } from 'table-xlsx';
import * as XLSX from '@pengchen/xlsx';
import ColumnSetting, { getCachedColumnSetting } from './components/ColumnSetting';
import { cloneDeep } from 'lodash';
import { useOutletContext } from 'react-router-dom';
import { AnalysisContext } from './AnalysisPortal';
import { filterNull } from '@/utils';
import layer from '@/utils/layer';
import { useUserInfo } from '@/hooks/hooks';
import { formatAnalysisTableRows, formatAnalysisTableValue } from './format';

const allRankEmpty = {
  t100Rate: '-',
  t200Rate: '-',
  t300Rate: '-',
  t400Rate: '-',
  t500Rate: '-',
  t1000Rate: '-',
  p10Rate: '-',
  p15Rate: '-',
  t2500Rate: '-',
  t4000Rate: '-',
  e1000Rate: '-'
};

const COURSE_SETTING_KEYS = ['CourseAvg', 'CourseRank'];
const EXPORT_BASIC_DATA_INDEX_LIST = [
  'schoolCode',
  'areaCode',
  'schoolShortname',
  'maxScore',
  'totalStuCount',
  'presentStuCount',
  'absentStuCount'
];
const EXPORT_PASS_DATA_INDEX_LIST = [
  'seniorLineStuCount',
  'seniorLineRate',
  'seniorLineRateRank',
  'allPassStuCount',
  'allPassRate',
  'allPassRateRank',
  'chinesePassRate',
  'chinesePassRateRank',
  'mathPassRate',
  'mathPassRateRank'
];
const EXPORT_SCORE_GROUP_NAME_LIST = ['t100', 't200', 't300', 't400', 't500', 't1000', 'p10', 'p15', 't2500', 't4000', 'e1000'];
const HEADER_FONT_SIZE = 12;
const BODY_FONT_SIZE = 11;

type AvgExportType = 'all' | 'area' | 'town';
type ExportColumn = ColumnConfig<SchoolStat> & {
  dataIndex: string;
  title: string;
};
type ExportWorkbook = {
  SheetNames: (string | number)[];
  Sheets: Record<string, unknown>;
};

const hasOwn = (target: Record<string, boolean>, key: string) =>
  Object.prototype.hasOwnProperty.call(target, key);

const applyCachedColumnSetting = (
  source: ColumnConfig<SchoolStat>[],
  options: {
    showGradeLine: boolean;
    isJunior: boolean;
  }
) => {
  const setting = getCachedColumnSetting(
    ALL_COURSE_AVG_COLUMN_SETTING_CONFIG.storageKey,
    getColumnSetting(options),
    ALL_COURSE_AVG_COLUMN_SETTING_CONFIG.version
  );
  const activeMap = setting.reduce((a, c) => {
    return {
      ...a,
      [c.dataIndex]: c.active
    };
  }, {} as Record<string, boolean>);

  return source.map(i => {
    const dataIndex = String(i.dataIndex || '');
    const selfMatched = hasOwn(activeMap, dataIndex);
    const column = {
      ...i,
      active: selfMatched ? activeMap[dataIndex] : i.active
    };
    let childrenChanged = false;

    if (i.children) {
      column.children = i.children.map(j => {
        const childDataIndex = String(j.dataIndex || '');
        const settingKey = Object.keys(activeMap).find(key => {
          if (key === childDataIndex) return true;
          return COURSE_SETTING_KEYS.includes(key) && childDataIndex.includes(key) && !childDataIndex.includes('_T');
        });

        if (!settingKey) return j;
        childrenChanged = true;
        return {
          ...j,
          active: activeMap[settingKey]
        };
      });

      if (!selfMatched && childrenChanged) {
        column.active = column.children.some(o => o.active);
      }
    }

    return column;
  });
};

const normalizeExportColumn = (column: ColumnConfig<SchoolStat>): ExportColumn => ({
  ...column,
  dataIndex: String(column.dataIndex || ''),
  title: String(column.title)
});

const buildActiveExportColumns = (source: ColumnConfig<SchoolStat>[]) => {
  return cloneDeep(source).filter(i => i.active).reduce((a, curr) => {
    if (!curr.children) {
      return [...a, normalizeExportColumn(curr)];
    }

    const children = curr.children.filter(i => i.active).map(i => normalizeExportColumn({
      ...i,
      title: i.title.includes('-') ? i.title : curr.title + i.title,
      width: 100
    }));

    return [...a, ...children];
  }, [] as ExportColumn[]);
};

const expandNumberCountColumns = (source: ExportColumn[]) => {
  const columns = [...source];
  const idx = columns.findIndex(i => i.dataIndex === 'numberCount');
  if (idx === -1) return columns;

  columns.splice(idx, 1);
  const maxScoreIndex = columns.findIndex(i => i.dataIndex === 'maxScore');
  const schoolIndex = columns.findIndex(i => i.dataIndex === 'schoolShortname');
  const insertIndex = (maxScoreIndex > -1 ? maxScoreIndex : schoolIndex) + 1;
  columns.splice(insertIndex, 0, ...[
    { title: '总人数', dataIndex: 'totalStuCount' },
    { title: '实考人数', dataIndex: 'presentStuCount' },
    { title: '缺考人数', dataIndex: 'absentStuCount' }
  ] as ExportColumn[]);

  return columns;
};

const isBasicExportColumn = (column: ExportColumn) =>
  EXPORT_BASIC_DATA_INDEX_LIST.includes(column.dataIndex);

const isPassExportColumn = (column: ExportColumn) => {
  if (isBasicExportColumn(column)) return true;
  if (EXPORT_PASS_DATA_INDEX_LIST.includes(column.dataIndex)) return true;
  return EXPORT_SCORE_GROUP_NAME_LIST.includes(String(column.fathername || ''));
};

const isAvgExportColumn = (column: ExportColumn) => {
  if (isBasicExportColumn(column)) return true;
  return /CourseAvg$|CourseRank$/.test(column.dataIndex);
};

const measureText = (text: unknown) => {
  const str = text === null || text === undefined ? '' : String(text);
  let units = 0;
  for (const ch of str) {
    units += /[\u3000-\u303f\u3400-\u9fff\uff00-\uffef]/.test(ch) ? 2 : 1;
  }
  return units;
};

const setExportColumnWidth = (columns: ExportColumn[], rows: SchoolStat[]) => {
  return columns.map(col => {
    let maxUnits = measureText(col.title);
    rows.forEach(row => {
      const value = formatAnalysisTableValue(row[col.dataIndex], col);
      maxUnits = Math.max(maxUnits, measureText(value));
    });

    return {
      ...col,
      width: Math.min(Math.max(Math.round(maxUnits * 7 + 12), 40), 200)
    };
  });
};

const withTitleColumnRender = (columns: ExportColumn[]) => {
  return columns.map(i => {
    if (i.dataIndex !== 'schoolCode') return i;

    return {
      ...i,
      render: (value: string, _: SchoolStat, index: number) => {
        if (index === 0) {
          return {
            children: value || '全市',
            props: {
              style: {
                borderStyle: 'thin',
                borderColorRgb: '000',
                alignmentHorizontal: 'center',
                fontName: '宋体'
              },
              colSpan: columns.length
            }
          };
        }
        return value;
      }
    };
  });
};

const setWorkbookFontSize = (workbook: ExportWorkbook) => {
  Object.values(workbook.Sheets).forEach(sheet => {
    const sheetRecord = sheet as Record<string, { s?: { font?: { bold?: boolean; sz?: number } } }>;
    Object.keys(sheetRecord).forEach(addr => {
      if (addr[0] === '!') return;
      const cell = sheetRecord[addr];
      if (cell?.s?.font) {
        cell.s.font.sz = cell.s.font.bold ? HEADER_FONT_SIZE : BODY_FONT_SIZE;
      }
    });
  });
};

const createExportSheet = (
  sheetName: string,
  sheetTitle: string,
  columns: ExportColumn[],
  rows: SchoolStat[]
) => {
  const sheetColumns = withTitleColumnRender(setExportColumnWidth(columns, rows));
  const dataSource = [
    { schoolCode: sheetTitle },
    sheetColumns.reduce((a, curr) => {
      return {...a, [curr.dataIndex]: curr.title };
    }, {} as Record<string, unknown>),
    ...rows.map(i => ({ ...i }))
  ];
  const workbook = getWorkbook({
    columns: sheetColumns as unknown as any,
    showHeader: false,
    raw: true,
    rowHpx: 15,
    onTxBodyRow: (_: unknown, index: number) => {
      if (index <= 1) {
        return {
          style: {
            borderStyle: 'thin',
            borderColorRgb: '000',
            alignmentHorizontal: 'center',
            fontBold: true,
            fontName: '宋体'
          }
        };
      }
      return {
        style: {
          borderStyle: 'thin',
          borderColorRgb: '000',
          alignmentHorizontal: 'center',
          fontName: '宋体'
        }
      };
    },
    dataSource: formatAnalysisTableRows(filterNull(dataSource), sheetColumns) as unknown as any
  }) as ExportWorkbook;

  setWorkbookFontSize(workbook);

  return workbook.Sheets[String(workbook.SheetNames[0] || sheetName)];
};

const AllCourseAvg = () => {
  const router = useRouter();
  const { name, examid, gradeDcode } = router.getSearchParams();
  const [list, setList] = useState<SchoolStat[]>([]);
  const [showGradeLine, setShowGradeLine] = useState(false);
  const [hasTownStat, setHasTownStat] = useState(false);
  const [columns, setColumns] = useState<ColumnConfig<SchoolStat>[]>([]);

  const context = useOutletContext<AnalysisContext>();
  const userInfo = useUserInfo();

  const load = async () => {
    try {
      layer.loading();
      const { data: prop }= await getRateConfig(examid);
      const { data } = await getAllCourseAvg(examid);
      setHasTownStat(data.townStatList.length !== 0);
      const courseColumns = data.coursetypeDcodeList.map(i => ({
        title: courseRecord[i] || '总分',
        name: i,
        active: true,
        children: (Object.keys(data.coursetypeModuleTypeInfo).includes(i) ? [
          ...data.coursetypeModuleTypeInfo[i].map(key => {
            return {
              title: courseModuleTypeRecord[key] + '-均分',
              active: true,
              dataIndex: key + 'CourseAvg',
              fathername: i
            };
          }),
          {
            title: `${courseRecord[i].slice(0, 1)}总-均分`,
            active: true,
            dataIndex: i + 'FullScoreCourseAvg',
            fathername: i
          }
        ] : [{
          title: '均分',
          dataIndex: i + 'FullScoreCourseAvg',
          fathername: i,
          active: true
        }]).concat({
          title: '排名',
          dataIndex: i + 'CourseRank',
          fathername: i,
          active: true
        })
      })) as ColumnConfig<SchoolStat>[];
      const sgl = prop.seniorLine !== null && gradeDcode.includes('J');
      setShowGradeLine(sgl);
      const columnOptions = {
        showGradeLine: sgl,
        isJunior: gradeDcode.includes('J')
      };
      const defaultColumns = getAllCourseAvgColumns(data as any, columnOptions);
      defaultColumns.splice(4, 0, ...courseColumns);
      setColumns(applyCachedColumnSetting(defaultColumns, columnOptions));

      // 合并科目
      const newList = [
        // 学校数据
        ...data.schoolStatList.map(i => ({ ...i, isSchool: true })),
        // 片区数据
        ...data.areaStatList.map(i => ({ ...i, schoolShortname: schoolAreaRecord[i.areaCode], isArea: true })),
        // 集团数据
        ...data.groupStatList.map(i => ({ ...i, schoolShortname: groupRecord[i.groupCode], isGroup: true })),
        // 乡镇数据 code有可能为空
        ...data.townStatList.map(i => ({ ...i, schoolShortname: townRecord[i.townCode] || '', isTown: true }))
      ];
      if (data.amountStat) newList.push({
        ...data.amountStat,
        ...allRankEmpty,
        isAll: true
      } as any);
      const l = newList.map(i => {
        return {
          ...i,
          ...data.coursetypeDcodeList.reduce((a, c) => {
            return {
              ...a,
              ...data.coursetypeModuleTypeInfo[c]?.reduce((acc, crr) => {
                return {
                  ...acc,
                  [`${crr}CourseAvg`]: i?.avgInfoList?.find(j => j.coursetypeDcode === c)?.moduleScoreAvgList.find(f => f.scoreModuleType === crr)?.scoreAvg
                };
              }, {} as any),
              [`${c}FullScoreCourseAvg`]: i?.avgInfoList?.find(j => j.coursetypeDcode === c)?.scoreAvg,
              [`${c}CourseRank`]: i?.avgInfoList?.find(j => j.coursetypeDcode === c)?.scoreAvgRank
            };
          }, {})
        };
      });
      setList(filterNull(l) as SchoolStat[]);
    } finally {
      layer.closeLoading();
    }
  };

  useEffect(() => {
    load();
  }, []);

  const secondRow = useMemo(() => {
    return columns.reduce((a, c) => {
      if (c.children) {
        return [...a, ...c.children];
      }
      else {
        return a;
      }
    }, [] as ColumnConfig<SchoolStat>[]);
  }, [columns]);

  const dataIndexArr = useMemo(() => {
    return columns.reduce((a, c) => {
      if (c.children) {
        return [...a, ...c.children.filter(i => i.active)];
      } else if (c.active) {
        return [...a, c];
      } else {
        return a;
      }
    }, [] as ColumnConfig<SchoolStat>[]);
  }, [columns, list]);

  // 导出学生成绩表
  const [exportStudentScoreLoading, setExportStudentScoreLoading] = useState('');
  const exportStudentScoreTable = (type: string) => {
    setExportStudentScoreLoading(type);
    instance.get('/teacher/exam/statistic/export/studentscore/whole', {
      params: {
        examId: examid,
        scoreType: type
      },
      responseType: 'blob'
    }).then(({ data }) => {
      const file = new FileReader();
      file.readAsText(data, 'utf-8');
      file.onload = () => {
        try {
          const res = JSON.parse(file.result as string);
          if (res.code && res.code === 'FAILURE') {
            layer.error(res.msg);
          }
        } catch {
          downloadFileByBlob(data, decodeURI(name) + `-学生成绩表(${type === 'ORIGINAL' ? '原始' : '重新计算'}).xlsx`);
        }
      };
    }).finally(() => {
      setExportStudentScoreLoading('');
    });
  };

  const exportAvgTable = (type: AvgExportType = 'all') => {
    const activeColumns = expandNumberCountColumns(buildActiveExportColumns(columns));
    // 按照导出类型筛选数据
    let l = [] as typeof list;
    const examName = decodeURI(name);
    let filename = examName;
    let areaColumnTitle = '片区';
    if (type === 'area') {
      const __list = cloneDeep(list);
      const schools = __list.filter(i => i.isSchool).sort((a, b) => (schoolAreaSeq[a.areaCode] || -1) - (schoolAreaSeq[b.areaCode] || -1));
      const areas = __list.filter(i => i.isArea);
      let areaCode = '';
      // eslint-disable-next-line no-shadow
      const sorted = schools.reduce((a, c, index) => {
        c.isSchool = false;
        c.isArea = true;
        if (areaCode === '') {
          areaCode = c.areaCode;
          return [c];
        } else if (areaCode !== c.areaCode) {
          const item = areas.find(i => i.areaCode === areaCode)!;
          areaCode = c.areaCode;
          return [...a, item, c];
        } else if (index === schools.length - 1) {
          const item = areas.find(i => i.areaCode === areaCode)!;
          return [...a, c, item];
        } else return [...a, c];
      }, [] as typeof list);
      const all = list.filter(i => i.isAll);
      l = [...sorted, ...all];
      filename += '-片区学科均分表';
    }
    if (type === 'town') {
      if (!hasTownStat) {
        message.error('暂无乡镇分析');
        return false;
      }
      areaColumnTitle = '乡镇';
      const __list = cloneDeep(list);
      const schools = __list.filter(i => i.isSchool).sort((a, b) => (townSeq[a.townCode] || Infinity) - (townSeq[b.townCode] || Infinity));
      const towns = __list.filter(i => i.isTown);
      let townCode = '' as ID;
      // eslint-disable-next-line no-shadow
      const sorted = schools.reduce((a, c, index) => {
        c.isSchool = false;
        c.isTown = true;
        if (townCode === '') {
          townCode = c.townCode;
          return [c];
        } else if (townCode !== c.townCode) {
          const item = towns.find(i => i.townCode === townCode);
          townCode = c.townCode;
          return item ? [...a, item, c] : [...a, c];
        } else if (index === schools.length - 1) {
          const item = towns.find(i => i.townCode === townCode);
          townCode = c.townCode;
          return item ? [...a, c, item] : [...a, c];
        } else {
          return [...a, c];
        }
      }, [] as typeof list);
      const all = list.filter(i => i.isAll);
      l = [...sorted, ...all];
      filename += '-乡镇学科均分表';
    }
    if (type === 'all') {
      l = list;
      filename += '-学科均分表';
    }

    const exportColumns = activeColumns.map(i => {
      if (i.dataIndex !== 'areaCode') return i;
      return {
        ...i,
        title: areaColumnTitle
      };
    });
    const passColumns = exportColumns.filter(isPassExportColumn);
    const avgColumns = exportColumns.filter(isAvgExportColumn);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      createExportSheet('普高合格率优生情况', `${examName}普高合格率优生情况`, passColumns, l),
      '普高合格率优生情况'
    );
    XLSX.utils.book_append_sheet(
      workbook,
      createExportSheet('学科均分', `${examName}学科均分`, avgColumns, l),
      '学科均分'
    );
    XLSX.writeFile(workbook, filename + '.xlsx');
    addLogoPt(examid, `${decodeURI(name)}-学科均分表`);
  };

  const onColumnChange = (checked: boolean, dataIndex: string) => {
    const newColumns= cloneDeep(columns);
    newColumns.forEach(i => {
      if (i.dataIndex === dataIndex) {
        i.active = checked;
      } else if (i.children) {
        i.children.forEach(j => {
          if (j.dataIndex === dataIndex) {
            j.active = checked;
            return;
          }
          if (dataIndex.includes('Course')) {
            if ((j.dataIndex as string).includes(dataIndex) && !(j.dataIndex as string).includes('_T')) {
              j.active = checked;
            }
          }
        });
        i.active = i.children.some(o => o.active);
      }
    });
    setColumns(newColumns);
  };

  const columnReset = (checked: boolean, dataIndex: string[]) => {
    const newColumns = cloneDeep(columns);
    dataIndex.forEach(d => {
      newColumns.forEach(i => {
        if (i.dataIndex === d) {
          i.active = checked;
        } else if (i.children) {
          i.children.forEach(j => {
            if (j.dataIndex === d) {
              j.active = checked;
              return;
            }
            if (d.includes('Course')) {
              if ((j.dataIndex as string).includes(d) && !(j.dataIndex as string).includes('_T')) {
                j.active = checked;
              }
            }
          });
          i.active = i.children.some(o => o.active);
        }
      });
    });
    setColumns(newColumns);
  };

  const [exportLoading, setExportLoading] = useState(false);
  const exportCourseScore = async () => {
    try {
      setExportLoading(true);
      const { data, msg } = await exportCourseScoreZip(examid as ID);
      if (!data) {
        layer.error(msg);
        return false;
      }
      downloadFileByUrl(data.zipFileUrl, data.zipFileName);
    } finally {
      setExportLoading(false);
    }
  };
  return (
    <>
      <div className={style.title}>
        <div>学科均分</div>
        <div>
          <span>导出:</span>
          {
            context.canExportStudentScore &&
            <>
              <Button loading={exportStudentScoreLoading === 'ORIGINAL'} onClick={() => exportStudentScoreTable('ORIGINAL')}>学生成绩表(原始)</Button>
              <Button loading={exportStudentScoreLoading === 'FINAL'} onClick={() => exportStudentScoreTable('FINAL')}>学生成绩表(重新计算)</Button>
            </>
          }
          {
            userInfo.userType === 'EDU' && <Dropdown
              overlay={
                () => <Menu>
                  <Menu.Item onClick={() => exportAvgTable()}>按全市</Menu.Item>
                  <Menu.Item onClick={() => exportAvgTable('area')}>按片区</Menu.Item>
                  <Menu.Item onClick={() => exportAvgTable('town')}>按乡镇</Menu.Item>
                </Menu>
              }
            >
              <Button>学科均分表</Button>
            </Dropdown>
          }
          {
            userInfo.userType === 'TEACHER' && <Button onClick={() => exportAvgTable()} >学科均分表</Button>
          }
          {
            userInfo?.userType === 'TEACHER' && context.exportQuestionScore && <Button onClick={exportCourseScore} loading={exportLoading}>小题得分明细</Button>
          }
          <Divider type="vertical" />
          <ColumnSetting
            onColumnChange={onColumnChange}
            onReset={columnReset}
            showGradeLine={showGradeLine}
            isJunior={gradeDcode.includes('J')}
            storageKey={ALL_COURSE_AVG_COLUMN_SETTING_CONFIG.storageKey}
            storageVersion={ALL_COURSE_AVG_COLUMN_SETTING_CONFIG.version}
          />
        </div>
      </div>
      <div className={style.table}>
        <Wrapper>
          <div className={style.tableWrapper}>
            <table className={style.uniTable}>
              <thead>
                <tr>
                  {
                    columns.filter(i => i.active).map((i, index) => (
                      <th
                        key={index}
                        colSpan={(i.children && i.children.filter(o => o.active).length) || 1}
                        rowSpan={!i.children ? 2 : 1}
                        style={{width: `${i.width || (i.children?.length && i.children?.filter(o => o.active).length * 60 + 60) || 80}px`}}
                      >
                        {i.title}
                      </th>
                    ))
                  }
                </tr>
                <tr>
                  {
                    secondRow.filter(i => i.active).map((i, index) => (
                      <th
                        key={index}
                        style={{width: `${i.width || 120}px`}}
                      >
                        {i.title}
                      </th>
                    ))
                  }
                </tr>
              </thead>
              <tbody>
                {
                  list.map((i, index) => (
                    <tr
                      key={i.schoolId + index}
                      className={style[i.isAll ? 'margin-top' : '']}
                    >
                      {
                        dataIndexArr.map(j=> (
                          <td
                            key={j.dataIndex as string}
                          >
                            { formatAnalysisTableValue(j.render ? j.render('', i, index) as unknown as any : i[j.dataIndex as string], j) as unknown as any }
                            {/* {j.dataIndex} */}
                          </td>
                        ))
                      }
                    </tr>
                  ))
                }
                {
                  list.length === 0 && <tr><td colSpan={dataIndexArr.length}>暂无数据</td></tr>
                }
              </tbody>
            </table>
          </div>
        </Wrapper>
      </div>
    </>
  );
};

export default AllCourseAvg;
